import { useState } from 'react';
import type { PickupLocation, WeekHours } from '@skytech/shared';

import { groupHours } from './hours';
import { usePickupLocations } from './api';
import { Modal } from '../../components/ui/Modal';

/**
 * Store pickup entry point (BOPIS, §4.6) — a header chip showing the primary location with a
 * live open/closed status, opening a dialog that lists every pickup location with address,
 * hours and directions. Lets customers know where + when to collect (research: store-locator
 * + clear hours + directions are the core BOPIS UX).
 */
/** A Date whose *local* fields read as Nairobi wall-clock time (Nairobi is UTC+3, no DST). */
function nairobiNow(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' }));
}

/** Current open/closed from per-day hours, evaluated in Africa/Nairobi wall-clock time. */
function storeStatus(hours: WeekHours | null): { open: boolean; label: string } {
  if (!hours) return { open: false, label: 'Hours unavailable' };
  const nbo = nairobiNow();
  const today = hours[String(nbo.getDay()) as keyof WeekHours];
  if (!today) return { open: false, label: 'Closed today' };
  const hm = `${String(nbo.getHours()).padStart(2, '0')}:${String(nbo.getMinutes()).padStart(2, '0')}`;
  if (hm >= today.open && hm < today.close)
    return { open: true, label: `Open · until ${today.close}` };
  if (hm < today.open) return { open: false, label: `Closed · opens ${today.open}` };
  return { open: false, label: 'Closed now' };
}

function StoreIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden
      className={className}
    >
      <path d="M4 9l1-4h14l1 4M4 9v10h16V9M4 9h16M9 19v-5h6v5" strokeLinejoin="round" />
    </svg>
  );
}

export function StorePickup({ compact = false }: { compact?: boolean }): JSX.Element | null {
  const { data: locations = [] } = usePickupLocations();
  const [open, setOpen] = useState(false);

  if (locations.length === 0) return null;
  const primary = locations[0]!;
  const status = storeStatus(primary.hours);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-11 items-center gap-2 rounded-md px-2 py-1 text-left text-navy-700 transition-colors hover:bg-cloud"
        aria-label="Store pickup locations"
      >
        <StoreIcon className="shrink-0" />
        {compact ? (
          <span className="whitespace-nowrap text-xs text-navy-700">
            Store pickup <span className="text-neutral">· {status.open ? 'Open' : 'Closed'}</span>
          </span>
        ) : (
          <span className="leading-tight">
            <span className="flex items-center gap-1 text-[11px] text-neutral">
              Pickup
              <span
                className={`inline-flex items-center gap-1 font-medium ${status.open ? 'text-green-ink' : 'text-danger'}`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${status.open ? 'bg-green-600' : 'bg-danger'}`}
                  aria-hidden
                />
                {status.open ? 'Open' : 'Closed'}
              </span>
            </span>
            <span className="flex items-center gap-1 text-sm font-medium text-navy-900">
              {primary.name}
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </span>
        )}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        label="Store pickup locations"
        className="w-full max-w-xl"
      >
        <div className="flex max-h-[85vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-frost bg-white px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky-500/12 text-sky-ink">
                <StoreIcon />
              </span>
              <div>
                <h2 className="text-lg font-medium text-navy-900">Collect in store</h2>
                <p className="text-sm text-neutral">
                  Buy online, pick up at{' '}
                  {locations.length === 1 ? 'our store' : `any of our ${locations.length} stores`} —
                  no delivery fee.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="tap-target -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-neutral transition-colors hover:bg-cloud hover:text-navy-900"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Locations */}
          <div className="space-y-4 overflow-y-auto bg-cloud px-6 py-5">
            {locations.map((loc) => (
              <LocationCard key={loc.id} loc={loc} />
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}

function LocationCard({ loc }: { loc: PickupLocation }): JSX.Element {
  const status = storeStatus(loc.hours);
  const today = nairobiNow().getDay();

  return (
    <div className="rounded-xl border border-frost bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-medium text-navy-900">{loc.name}</h3>
          <p className="mt-1 flex items-start gap-1.5 text-sm text-navy-700">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              aria-hidden
              className="mt-0.5 shrink-0 text-sky-ink"
            >
              <path d="M12 21s7-6.3 7-11a7 7 0 10-14 0c0 4.7 7 11 7 11z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            <span>
              {loc.addressLine}
              {loc.landmark && <span className="block text-neutral">{loc.landmark}</span>}
            </span>
          </p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${status.open ? 'bg-green-600/12 text-green-ink' : 'bg-danger/10 text-danger'}`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${status.open ? 'bg-green-600' : 'bg-danger'}`}
            aria-hidden
          />
          {status.label}
        </span>
      </div>

      {loc.hours && (
        <div className="mt-4 rounded-lg bg-cloud px-3.5 py-3">
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral">
            Opening hours
          </p>
          <dl className="space-y-1 text-sm">
            {groupHours(loc.hours).map((r) => {
              const isToday = r.days.includes(today);
              return (
                <div
                  key={r.label}
                  className={`flex justify-between gap-4 ${isToday ? 'font-medium text-navy-900' : 'text-navy-700'}`}
                >
                  <dt className={isToday ? '' : 'text-neutral'}>
                    {r.label}
                    {isToday && (
                      <span className="ml-1.5 rounded bg-sky-500/12 px-1.5 py-0.5 text-[10px] font-medium text-sky-ink">
                        Today
                      </span>
                    )}
                  </dt>
                  <dd>{r.value}</dd>
                </div>
              );
            })}
            {loc.holidaysNote && (
              <div className="flex justify-between gap-4 border-t border-frost pt-1.5 text-neutral">
                <dt>Holidays</dt>
                <dd className="text-navy-700">{loc.holidaysNote.replace(/^holidays:?\s*/i, '')}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* CTAs — directions is the primary, appealing action */}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        {loc.mapUrl && (
          <a
            href={loc.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-sky-600"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden
            >
              <path d="M9 20l-5.5 2 2-16L9 4l6 2 5.5-2-2 16L15 22z" strokeLinejoin="round" />
              <path d="M9 4v16M15 6v16" />
            </svg>
            Get directions
          </a>
        )}
        {loc.phone && (
          <a
            href={`tel:${loc.phone}`}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-frost px-4 py-2.5 text-sm font-medium text-navy-900 transition-colors hover:border-sky-500 hover:text-sky-ink"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              aria-hidden
            >
              <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012 4.2 2 2 0 014 2h3a2 2 0 012 1.7c.1.9.4 1.8.7 2.7a2 2 0 01-.5 2.1L8.1 9.9a16 16 0 006 6l1.4-1.1a2 2 0 012.1-.5c.9.3 1.8.6 2.7.7A2 2 0 0122 16.9z" />
            </svg>
            Call
          </a>
        )}
        {loc.whatsapp && (
          <a
            href={`https://wa.me/${loc.whatsapp.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Message ${loc.name} on WhatsApp`}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-frost px-4 py-2.5 text-sm font-medium text-navy-900 transition-colors hover:border-green-600 hover:text-green-ink"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden
              className="text-green-600"
            >
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.8 14.16c-.24.68-1.42 1.32-1.95 1.36-.5.04-.97.28-3.28-.68-2.77-1.09-4.5-3.94-4.64-4.13-.13-.19-1.11-1.48-1.11-2.82s.7-2 .95-2.28c.24-.28.53-.35.71-.35.18 0 .36 0 .51.01.16.01.38-.06.6.46.24.56.79 1.94.86 2.08.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.56.16.28.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.21 1.37.28.14.44.12.6-.07.18-.21.7-.81.89-1.09.18-.28.37-.23.6-.14.24.09 1.53.72 1.79.85.26.14.44.21.5.32.06.11.06.65-.18 1.33z" />
            </svg>
            WhatsApp
          </a>
        )}
      </div>
    </div>
  );
}
